import React, { useEffect, useRef } from "react";
import { Box, Popper, Typography, useTheme } from "@mui/material";
import { User as UserIcon } from "lucide-react";
import type { HcpSearchResult } from "../../../types/hcpMention";
import {
  avatarSx,
  duplicateSx,
  getDetailSx,
  getHintSx,
  getLabelSx,
  getOwnerSx,
  getPaperSx,
  getPopperSx,
  getRowSx,
  listSx,
} from "./styles";

type HcpMentionPickerProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  results: HcpSearchResult[];
  query: string;
  selectedIndex: number;
  onHoverIndex: (index: number) => void;
  onSelect: (result: HcpSearchResult) => void;
};

export const HcpMentionPicker: React.FC<HcpMentionPickerProps> = ({
  open,
  anchorEl,
  results,
  query,
  selectedIndex,
  onHoverIndex,
  onSelect,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const selectedRowRef = useRef<HTMLDivElement | null>(null);
  const trimmedQuery = query.trim();
  const pickerWidth = Math.max(anchorEl?.clientWidth ?? 0, 260);

  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, results.length]);

  if (!open || !anchorEl) return null;

  let body: React.ReactNode;
  if (!trimmedQuery) {
    body = (
      <Typography sx={getHintSx(isDarkMode)}>
        Search by HCP name, account, or city
      </Typography>
    );
  } else if (results.length === 0) {
    body = (
      <Typography sx={getHintSx(isDarkMode)}>No matching HCPs</Typography>
    );
  } else {
    body = (
      <Box sx={listSx} role="listbox" aria-label="HCP mentions">
        {results.map((result, index) => {
          const selected = index === selectedIndex;
          return (
            <Box
              key={`${result.mentionToken}-${index}`}
              ref={selected ? selectedRowRef : undefined}
              role="option"
              aria-selected={selected}
              sx={getRowSx(isDarkMode, selected)}
              onMouseEnter={() => onHoverIndex(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                onSelect(result);
              }}
            >
              <Box sx={avatarSx} aria-hidden>
                <UserIcon size={18} strokeWidth={2} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={getLabelSx(isDarkMode)} noWrap>
                  {result.label}
                </Typography>
                {result.detail ? (
                  <Typography sx={getDetailSx(isDarkMode)}>
                    {result.detail}
                  </Typography>
                ) : null}
                {result.ownerName ? (
                  <Typography sx={getOwnerSx(isDarkMode)}>
                    {result.ownerName}
                  </Typography>
                ) : null}
                {result.duplicateAssignment ? (
                  <Typography sx={duplicateSx}>
                    Duplicate assignment
                    {result.duplicateAssignmentCount > 1
                      ? ` (${result.duplicateAssignmentCount})`
                      : ""}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="top-start"
      sx={getPopperSx()}
      modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
    >
      <Box data-hcp-mention-picker="true" sx={getPaperSx(isDarkMode, pickerWidth)}>{body}</Box>
    </Popper>
  );
};
